/**
 * Display label for Task.requester (assigning practitioner / organization).
 * Used by Measures assessment log and chart bundle assessments.
 */

import type { MedplumClient } from '@medplum/core';
import type { Practitioner, Reference, Task } from '@medplum/fhirtypes';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';

/**
 * Synchronous label when Practitioners are already loaded (e.g. chart bundle batch).
 */
export function taskRequesterLabel(
  requester: Reference | undefined,
  practitionerById: Map<string, Practitioner>,
): string {
  if (!requester) return '—';
  const d = requester.display?.trim();
  const ref = requester.reference;
  if (!ref) return d || '—';
  if (ref.startsWith('Practitioner/')) {
    const id = ref.replace('Practitioner/', '');
    const p = practitionerById.get(id);
    if (p) {
      const n = formatPractitionerStaffLabel(p);
      if (n) return n;
    }
    if (d) return d;
    return id;
  }
  if (d) return d;
  if (ref.startsWith('Organization/')) {
    return ref.replace('Organization/', '');
  }
  return ref.split('/').pop() ?? '—';
}

/**
 * Async label with read + cache (e.g. MeasuresTab task loop).
 */
export async function resolveTaskRequesterLabel(
  medplum: MedplumClient,
  requester: Task['requester'],
  cache: Map<string, string>,
): Promise<string> {
  if (!requester) return '—';
  const d = requester.display?.trim();
  const ref = requester.reference;
  if (!ref) return d || '—';
  if (ref.startsWith('Practitioner/')) {
    const id = ref.replace('Practitioner/', '');
    const hit = cache.get(`Practitioner/${id}`);
    if (hit) return hit;
    try {
      const p = await medplum.readResource('Practitioner', id);
      const label = formatPractitionerStaffLabel(p) || id;
      cache.set(`Practitioner/${id}`, label);
      return label;
    } catch {
      const fallback = d || id;
      cache.set(`Practitioner/${id}`, fallback);
      return fallback;
    }
  }
  if (d) return d;
  if (ref.startsWith('Organization/')) {
    const id = ref.replace('Organization/', '');
    const key = `Organization/${id}`;
    const hit = cache.get(key);
    if (hit) return hit;
    try {
      const o = await medplum.readResource('Organization', id);
      const label = o.name ?? id;
      cache.set(key, label);
      return label;
    } catch {
      cache.set(key, id);
      return id;
    }
  }
  return ref.split('/').pop() ?? '—';
}
